import { Enquiry } from "../../components/Enquiry";
import { Footer } from "../../components/Footer";
import { Layout } from "../../components/Layout";
import LogoMarquee from "@/components/Sections/LogoMarquee";
import { CTA_LINK, CTA_TEXT, DEMO_TEXT } from "@/components/layout/PageLayout";
import { CaseStudyGrid } from "@/components/layout/sections/CaseStudyGrid";
import { Hero } from "@/components/layout/sections/Hero";
import { ProductFooter } from "@/components/layout/sections/ProductFooter";
import Section from "@/components/layout/sections/Section/Section";
import TitleSection from "@/components/layout/text/TitleSection/TitleSection";
import { faShareNodes, faShieldCheck, faUnlock } from "@icons/regular";
import { Button, Icon } from "@ui";
import { Link, graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";

const securityFeatures = [
  {
    id: 1,
    icon: faShareNodes,
    title: "Sharing & previews",
    description: "Get approvals faster with instant, shareable, and secure preview URLs for your stakeholders.",
  },
  {
    id: 2,
    icon: faUnlock,
    title: "Set permissions",
    description:
      "Manage access to content, publishing, and settings with clear, customisable roles. Set permissions at the team or story level with admin controls.",
  },
  {
    id: 3,
    icon: faShieldCheck,
    title: "Built-in safety for teams",
    description: (
      <>
        Shorthand leads with top-tier security designed to protect your stories and data, giving you peace of mind while you create and
        publish. <Link to="/features/security">Learn more</Link>.
      </>
    ),
  },
];

const seamlessCollaborationFeatures = [
  {
    name: "Concurrent editing",
    description: "Just like in your favourite design and document editing tools, view, and create content simultaneously.",
  },
  {
    name: "Commenting",
    description: "Add context, feedback, or to-dos right inside the editor. Keep your creative conversations close to the work.",
  },
  {
    name: "Workspace section templates",
    description: "Save and share your team’s best work with reusable sections that streamline and standardise storytelling.",
  },
  {
    name: "Copy stories between teams",
    description: "Working across multiple teams or clients? Easily copy stories between team spaces for a smooth workflow.",
  },
  {
    name: "One-click invite",
    description: "Quickly invite new team members to collaborate on stories.",
  },
];

const smartWorkspaceFeatures = [
  {
    name: "Intuitive dashboard",
    description: "Your ultimate command centre. Quickly access and manage stories, filter by tags, and navigate projects with ease.",
  },
  {
    name: "Story tags",
    description: "Organise and find stories quickly with smart tagging by client, campaign, format, status, and more.",
  },
  {
    name: "Story resource links",
    description:
      "Add important docs, guidelines, or creative briefs directly to stories so your team always has the context they need.",
  },
  {
    name: "Bulk story operations",
    description: "Make sweeping changes faster — delete, move, or organise multiple stories at once to save hours of busywork.",
  },
  {
    name: "Multiple workspaces",
    description: "Intuitively work across multiple teams, clients, or brands with multiple Shorthand workspaces.",
  },
];

const FeaturesCollaboration: React.FC<{ location: any }> = props => {
  const [showPortal, setShowPortal] = React.useState(false);
  const prePopulatedFields = React.useRef<Record<any, string>>({});

  function handleShowPortal(fields?: Record<any, string>): void {
    setShowPortal(true);
    prePopulatedFields.current = fields || {};
  }

  const portalProps = {
    type: "general-enquiry",
    subject: "general-enquiry",
    prePopulatedFields: prePopulatedFields.current,
    onClose: () => {
      setShowPortal(false);
      prePopulatedFields.current = {};
    },
  };

  return (
    <Layout location={props.location} theme="dark-theme">
      {showPortal && ReactDOM.createPortal(<Enquiry {...portalProps} />, document.getElementById("modal"))}
      <main id="main" className="marketing-content container-fluid">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-secondary body-marketing-features body-light hide-elevio",
          }}
        ></Helmet>
        <Hero
          imagePlacement="bottom"
          actionsBelowImage={false}
          title={
            <>
              The best stories are built <span className="tw-font-PassengerSerif tw-italic">together</span>
            </>
          }
          description="Great stories don’t happen in isolation. Whether you’re part of a content team, agency, or newsroom, Shorthand makes it easy to build, review, and publish stunning visual stories — together, in real time."
          image={
            <StaticImage
              src={`../../../static/product/collaborate/collaboration-hero.png`}
              alt="Photo collage of Shorthand customer stories showing different branding styles"
              width={2432}
              height={1442}
              className="tw-w-[76rem] tw-rounded-md tw-bg-white/5 tw-shadow-2xl tw-ring-1 tw-ring-white/10"
            />
          }
          actions={
            <>
              <Button asChild variant="primary-gradient" size="lg">
                <a href={CTA_LINK} className="tw-no-underline hover:tw-no-underline">
                  {CTA_TEXT}
                </a>
              </Button>
              <Button
                variant="secondary-outline"
                style={{ border: "1px solid white" }}
                size="lg"
                onClick={() =>
                  handleShowPortal({
                    interestedIn: "demo",
                    message: "booking a demo of the Shorthand platform",
                  })
                }
              >
                {DEMO_TEXT}
              </Button>
            </>
          }
        />

        <Section>
          <TitleSection headingElement="h2" title="Trusted by the world’s leading brands" />
          <LogoMarquee />
        </Section>

        <Section>
          <TitleSection
            title={<>Seamless collaboration</>}
            description={"All the essentials, and more, to help your team move fast and create beautifully, without the bottlenecks."}
          />
          <div className="tw-mt-16 tw-space-y-12">
            <div className="tw--mx-6 lg:tw--mx-8">
              <StaticImage
                src={`../../../static/product/collaborate/collaboration-templates.png`}
                alt="A selection of collaboration templates inside the Shorthand editor"
                width={2432}
                height={1442}
                className="tw-h-auto tw-w-full tw-rounded-2xl tw-shadow-xl tw-ring-1 tw-ring-white/10"
              />
            </div>
            <div className="tw-grid tw-grid-cols-1 tw-gap-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-5">
              {seamlessCollaborationFeatures.map(feature => (
                <div key={feature.name} className="tw-space-y-2">
                  <p className="tw-text-lg tw-font-semibold tw-tracking-tight tw-text-[var(--foreground-color)]">{feature.name}</p>
                  <p className="tw-text-balance tw-text-base/6 tw-text-[var(--foreground-color-muted)]">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section>
          <TitleSection
            title={<>Smart workspace. Smarter team.</>}
            description={"Spend less time managing and more time creating in a workspace that scales with your team."}
          />
          <div className="tw-mt-16 tw-space-y-12">
            <div className="tw--mx-6 lg:tw--mx-8">
              <StaticImage
                src={`../../../static/product/collaborate/collaboration-editor.png`}
                alt="Dashboard view of the Shorthand workspace"
                width={2432}
                height={1442}
                className="tw-h-auto tw-w-full tw-rounded-2xl tw-shadow-xl tw-ring-1 tw-ring-white/10"
              />
            </div>
            <div className="tw-grid tw-grid-cols-1 tw-gap-10 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-5">
              {smartWorkspaceFeatures.map(feature => (
                <div key={feature.name} className="tw-space-y-2">
                  <p className="tw-text-lg tw-font-semibold tw-tracking-tight tw-text-[var(--foreground-color)]">{feature.name}</p>
                  <p className="tw-text-balance tw-text-base/6 tw-text-[var(--foreground-color-muted)]">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </Section>

        <Section>
          <TitleSection
            title="Easy access, tight security"
            description="Trust is built into the way you work. Give your team the freedom to create, knowing their work is protected."
          />
          <div className="tw-mt-16 tw-grid tw-grid-cols-1 tw-gap-8 sm:tw-grid-cols-3">
            {securityFeatures.map(feature => (
              <div
                className="tw-flex tw-flex-col tw-justify-start tw-gap-4 tw-rounded-2xl tw-bg-grey_scale-800/50 tw-p-8"
                key={`preview-feature-${feature.id}`}
              >
                <div className="tw-h-12 tw-w-12">
                  <Icon icon={feature.icon} className="tw-h-full tw-w-full tw-text-white" />
                </div>
                <h3 className="!tw-text-lg tw-font-semibold tw-tracking-tight tw-text-brand_scale-400">{feature.title}</h3>
                <span className="tw-max-w-[30ch] tw-text-base/6 tw-text-[var(--foreground-color)]">{feature.description}</span>
              </div>
            ))}
          </div>
        </Section>

        <CaseStudyGrid />
        <ProductFooter />
        <Footer className="mt-0" />
      </main>
    </Layout>
  );
};

export default FeaturesCollaboration;

export const query = graphql`
  query FeaturesCollaborationQuery {
    site {
      siteMetadata {
        name
        title
        tagline
        description
      }
    }
  }
`;
